
import React, { useState, useMemo } from 'react';
import { Ticket, TicketStatus, PaymentStatus, BuybackDeviceType, BuybackCondition, RepairStageStatus, AppSettings, CommunicationType, TicketTrendData, RepairBreakdownSegment, Employee, EmployeeRole, TicketPriority, Appointment, DeviceModelInfo, BuybackPricingFactors, AppointmentModalData } from '../types';
import { MetricCard } from './MetricCard';
import { RecentRepairsTable } from './RecentRepairsTable';
import { MOCK_TICKETS, MOCK_EMPLOYEES } from '../constants'; // Added MOCK_EMPLOYEES
import { QuickAppraisalWidget } from './QuickAppraisalWidget';
import type { QuickAppraisalInitialData } from '../App';
import { Button } from './Button';

// Icons
const ClipboardListIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
  </svg>
);
const CurrencyDollarIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 11.219 12.768 11 12 11c-.768 0-1.536.219-2.121.782l-.879.659M7.5 14.25l6-6M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const DocumentPlusIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);
const ChartPieIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2 text-slate-500" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z" />
  </svg>
);
const InboxArrowDownIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2 text-slate-500" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" />
 </svg>
);

// New Icons for Status Cards
const InboxIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859M12 3v10.5m0 0l-3-3m3 3l3-3" />
  </svg>
);
const CogIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12a7.5 7.5 0 0015 0m-15 0a7.5 7.5 0 1115 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077l1.41-.513m14.095-5.13l-1.41-.513M3 10.5h18M4.5 4.5l.47.187m14.554 9.626l-.47.187M9.75 21a8.25 8.25 0 0010.607-6.75M9.75 3a8.25 8.25 0 0110.607 6.75" />
  </svg>
);
const UserQuestionIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
  </svg>
);
const PackageIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10.5 11.25h3M12 15h.008M3.75 7.5h16.5M16.5 7.5V5.625C16.5 4.59 15.66 3.75 14.625 3.75h-5.25C8.34 3.75 7.5 4.59 7.5 5.625V7.5" />
  </svg>
);
const CheckBadgeIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const ArrowUpIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
  </svg>
);
const ArrowDownIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" />
  </svg>
);
const TrendingUpIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
  </svg>
);
const BanknotesIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
  </svg>
);
const CalendarDaysIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5M12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z" />
  </svg>
);

interface DashboardProps {
  allTickets: Ticket[];
  appointments: Appointment[];
  onOpenTicketDetails: (ticketId: string) => void;
  onOpenUpdateStatusModal: (ticketId: string, currentStatus: TicketStatus) => void;
  onViewCustomerProfile: (customerName: string) => void;
  onOpenPaymentModal: (ticketId: string, amountDue: number, customerName: string) => void;
  onOpenPrintModal: (title: string, content: React.ReactNode, contentClassName?: string) => void;
  onShowCustomerPortal?: (ticketId: string) => void;
  onAddInternalTicketNote: (ticketId: string, noteText: string, employeeName?: string) => void;
  onUpdateRepairStage: (ticketId: string, stageId: string, newStatus: RepairStageStatus, notes?: string) => void;
  onAddCommunicationLog: (ticketId: string, type: CommunicationType, content: string, recipient?: string, staffName?: string) => void;
  onOpenAppointmentModal: (data?: AppointmentModalData) => void;
  onNavigateToFullBuyback: (initialData?: QuickAppraisalInitialData) => void;
  appSettings: AppSettings;
  ticketTrendData: TicketTrendData | null;
  onShowUnpaidTickets: () => void;
  onNewTicket: () => void;
  onShowAppointments: () => void;
  repairBreakdownData: RepairBreakdownSegment[];
  currentUser: Employee | null;
  buybackDeviceModels: Record<BuybackDeviceType, DeviceModelInfo[]>;
  buybackPricingFactors: BuybackPricingFactors;
}

export const Dashboard: React.FC<DashboardProps> = ({
  allTickets,
  appointments,
  onOpenTicketDetails,
  onOpenUpdateStatusModal,
  onViewCustomerProfile,
  onOpenPaymentModal,
  onOpenPrintModal,
  onShowCustomerPortal,
  onAddInternalTicketNote,
  onUpdateRepairStage,
  onAddCommunicationLog,
  onOpenAppointmentModal,
  onNavigateToFullBuyback,
  appSettings,
  ticketTrendData,
  onShowUnpaidTickets,
  onNewTicket,
  onShowAppointments,
  repairBreakdownData,
  currentUser,
  buybackDeviceModels,
  buybackPricingFactors,
}) => {
  const [selectedRevenuePeriod, setSelectedRevenuePeriod] = useState<'today' | 'yesterday' | 'week' | 'month'>('today');

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const activeRepairs = useMemo(() => {
    return allTickets.filter(ticket => ticket.status !== TicketStatus.COMPLETED && ticket.status !== TicketStatus.CUSTOMER_DECLINED);
  }, [allTickets]);

  const newTicketsToday = useMemo(() => {
    return allTickets.filter(ticket => new Date(ticket.createdAt) >= today);
  }, [allTickets, today]);

  const calculateRevenueForPeriod = (period: 'today' | 'yesterday' | 'week' | 'month') => {
    let startDate: Date;
    const endDate = new Date(); // up to now for week/month/today, or end of yesterday
    endDate.setHours(23, 59, 59, 999);

    switch (period) {
      case 'today':
        startDate = new Date(today);
        break;
      case 'yesterday':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        endDate.setDate(today.getDate() - 1);
        break;
      case 'week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1)); // Monday as start of week
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      default:
        startDate = new Date(today);
    }
    startDate.setHours(0, 0, 0, 0);

    return allTickets
      .filter(ticket =>
        ticket.paymentStatus === PaymentStatus.PAID &&
        new Date(ticket.updatedAt) >= startDate &&
        new Date(ticket.updatedAt) <= endDate
      )
      .reduce((sum, ticket) => sum + ticket.amountDue, 0);
  };

  const revenueForSelectedPeriod = useMemo(() => {
    return calculateRevenueForPeriod(selectedRevenuePeriod);
  }, [allTickets, selectedRevenuePeriod]);

  const recentRepairs = useMemo(() => {
    return [...allTickets]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 5);
  }, [allTickets]);

  const upcomingAppointments = useMemo(() => {
    const now = new Date();
    return appointments
      .filter(apt => new Date(apt.date + 'T' + apt.startTime) >= now)
      .sort((a,b) => new Date(a.date + 'T' + a.startTime).getTime() - new Date(b.date + 'T' + b.startTime).getTime())
      .slice(0, 5);
  }, [appointments]);

  const getTechnicianName = (id?: string) => MOCK_EMPLOYEES.find(e => e.id === id)?.name || 'Unassigned';

  const {
      activeRepairs: showActiveRepairs,
      revenueSummary: showRevenueSummary,
      newRepairsToday: showNewRepairsToday,
      newTickets: showNewTickets,
      inProgressTickets: showInProgressTickets,
      waitingCustomerApprovalTickets: showWaitingCustomer,
      waitingForPartsTickets: showWaitingParts,
      readyForPickupTickets: showReadyForPickup,
      quickAppraisalWidget: showQuickAppraisal,
      repairTypeBreakdown: showRepairBreakdown,
      newCheckInsPanel: showNewCheckins,
  } = appSettings.dashboardCardVisibility;

  const unpaidReadyOrCompletedTickets = useMemo(() => {
    return allTickets.filter(ticket =>
        (ticket.status === TicketStatus.READY_FOR_PICKUP || ticket.status === TicketStatus.COMPLETED) &&
        ticket.paymentStatus === PaymentStatus.UNPAID &&
        ticket.amountDue > 0
    ).length;
  }, [allTickets]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {showActiveRepairs && (
            <MetricCard
            title="Active Repairs"
            value={activeRepairs.length}
            icon={<ClipboardListIcon className="w-8 h-8 text-[var(--color-primary)]" />}
            description={
                <button onClick={() => onOpenTicketDetails('')} className="text-xs text-[var(--color-primary)] hover:underline">
                    View All Active Repairs
                </button>
            }
            />
        )}
        {showRevenueSummary && (
            <MetricCard
            title="Revenue (Simulated)"
            value={`$${revenueForSelectedPeriod.toFixed(2)}`}
            icon={<CurrencyDollarIcon className="w-8 h-8 text-emerald-500" />}
            description={
                <select
                    value={selectedRevenuePeriod}
                    onChange={(e) => setSelectedRevenuePeriod(e.target.value as 'today'|'yesterday'|'week'|'month')}
                    className="text-xs text-slate-500 bg-transparent border-none focus:outline-none focus:ring-0 p-0 m-0 appearance-none"
                >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                </select>
            }
            />
        )}
         {showNewRepairsToday && (
            <MetricCard
            title="New Repairs Today"
            value={newTicketsToday.length}
            icon={<DocumentPlusIcon className="w-8 h-8 text-sky-500" />}
            description={
                <button onClick={() => onOpenTicketDetails('')} className="text-xs text-[var(--color-primary)] hover:underline">
                    View Today's Tickets
                </button>
            }
            />
        )}
        {ticketTrendData && (
             <MetricCard
                title="Tickets (Last 7 days)"
                value={ticketTrendData.currentPeriodCount}
                icon={<TrendingUpIcon className={`w-8 h-8 ${ticketTrendData.trendDirection === 'up' ? 'text-emerald-500' : ticketTrendData.trendDirection === 'down' ? 'text-red-500' : 'text-slate-500'}`} />}
                description={
                  <span className={`text-xs ${ticketTrendData.trendDirection === 'up' ? 'text-emerald-600' : ticketTrendData.trendDirection === 'down' ? 'text-red-600' : 'text-slate-500'}`}>
                    {ticketTrendData.trendDirection === 'up' && <ArrowUpIcon className="inline w-3 h-3 mr-1"/>}
                    {ticketTrendData.trendDirection === 'down' && <ArrowDownIcon className="inline w-3 h-3 mr-1"/>}
                    {ticketTrendData.percentageChange.toFixed(1)}% vs previous 7 days ({ticketTrendData.previousPeriodCount})
                  </span>
                }
            />
        )}

      </div>

      {/* Tickets By Status Section */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {showNewTickets && <MetricCard title="New" value={allTickets.filter(t => t.status === TicketStatus.NEW).length} icon={<InboxIcon className="w-6 h-6 text-sky-600"/>} className="bg-sky-50 border border-sky-200 hover:shadow-md"/>}
            {showInProgressTickets && <MetricCard title="In Progress" value={allTickets.filter(t => t.status === TicketStatus.IN_PROGRESS).length} icon={<CogIcon className="w-6 h-6 text-amber-600"/>} className="bg-amber-50 border border-amber-200 hover:shadow-md"/>}
            {showWaitingCustomer && <MetricCard title="Waiting Approval" value={allTickets.filter(t => t.status === TicketStatus.WAITING_FOR_CUSTOMER_APPROVAL).length} icon={<UserQuestionIcon className="w-6 h-6 text-orange-600"/>} className="bg-orange-50 border border-orange-200 hover:shadow-md"/>}
            {showWaitingParts && <MetricCard title="Waiting Parts" value={allTickets.filter(t => t.status === TicketStatus.WAITING_FOR_PARTS).length} icon={<PackageIcon className="w-6 h-6 text-purple-600"/>} className="bg-purple-50 border border-purple-200 hover:shadow-md"/>}
            {showReadyForPickup && <MetricCard title="Ready for Pickup" value={allTickets.filter(t => t.status === TicketStatus.READY_FOR_PICKUP).length} icon={<CheckBadgeIcon className="w-6 h-6 text-lime-600"/>} className="bg-lime-50 border border-lime-200 hover:shadow-md"/>}
        </div>


      {/* Main Dashboard Area (Recent Repairs & Quick Actions) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-lg">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-slate-700">Recent Repairs</h2>
                <Button variant="secondary" onClick={() => onOpenTicketDetails('')} className="text-xs">View All Tickets</Button>
            </div>
          <RecentRepairsTable
            tickets={recentRepairs}
            appSettings={appSettings}
            currentUser={currentUser}
            onOpenUpdateStatusModal={onOpenUpdateStatusModal}
            onViewCustomerProfile={onViewCustomerProfile}
            onOpenPaymentModal={onOpenPaymentModal}
            onOpenPrintModal={onOpenPrintModal}
            onShowCustomerPortal={onShowCustomerPortal}
            onAddInternalTicketNote={onAddInternalTicketNote}
            onUpdateRepairStage={onUpdateRepairStage}
          />
        </div>

        {/* Quick Actions & Info Panel */}
        <div className="space-y-6">
            {showNewCheckins && (
                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <h2 className="text-xl font-semibold text-slate-700 mb-4">New Check-ins Today</h2>
                    {newTicketsToday.length > 0 ? (
                        <ul className="space-y-2 max-h-40 overflow-y-auto">
                        {newTicketsToday.map(ticket => (
                            <li key={ticket.id} className="text-sm p-2 bg-slate-50 rounded-md hover:bg-slate-100 cursor-pointer" onClick={() => onOpenTicketDetails(ticket.id)}>
                                <p className="font-medium text-[var(--color-primary)] truncate">{ticket.subject}</p>
                                <p className="text-xs text-slate-500 truncate">{ticket.customer} - {ticket.deviceModel || 'N/A'}</p>
                            </li>
                        ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-slate-500">No new check-ins yet today.</p>
                    )}
                </div>
            )}

            {unpaidReadyOrCompletedTickets > 0 && (
                <div
                    className="bg-red-50 p-4 rounded-lg shadow-lg border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={onShowUnpaidTickets}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && onShowUnpaidTickets()}
                >
                    <div className="flex items-center">
                        <BanknotesIcon className="w-8 h-8 text-red-500 mr-3"/>
                        <div>
                            <h3 className="text-lg font-semibold text-red-700">Outstanding Payments</h3>
                            <p className="text-sm text-red-600">{unpaidReadyOrCompletedTickets} ticket(s) require payment.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Upcoming Appointments Panel */}
            <div className="bg-white p-6 rounded-lg shadow-lg">
                 <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-slate-700 flex items-center"><CalendarDaysIcon className="w-6 h-6 text-slate-500 mr-2"/> Upcoming Appointments</h2>
                    <Button variant="ghost" onClick={onShowAppointments} className="text-xs !text-[var(--color-primary)]">View Calendar</Button>
                </div>
                {upcomingAppointments.length > 0 ? (
                    <ul className="space-y-2 max-h-48 overflow-y-auto">
                        {upcomingAppointments.map(apt => (
                        <li key={apt.id} className="text-sm p-2.5 bg-slate-50 rounded-md hover:bg-slate-100 cursor-pointer border border-slate-100" onClick={() => onOpenAppointmentModal({appointment: apt})}>
                            <div className="flex justify-between items-center">
                                <p className="font-semibold text-[var(--color-primary)] truncate">{apt.serviceDescription}</p>
                                <span className="text-xs text-slate-500 whitespace-nowrap">{new Date(apt.date + 'T' + apt.startTime).toLocaleDateString([], {month: 'short', day:'numeric'})}, {apt.startTime}</span>
                            </div>
                            <p className="text-xs text-slate-600 truncate">Customer: {apt.customerName}</p>
                            <p className="text-xs text-slate-500 truncate">Tech: {getTechnicianName(apt.assignedTechnicianId)}</p>
                        </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-sm text-slate-500">No upcoming appointments scheduled.</p>
                )}
            </div>

        </div>
      </div>

      {showRepairBreakdown && repairBreakdownData.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-semibold text-slate-700 mb-4 flex items-center"><ChartPieIcon /> Repair Type Breakdown (All Tickets)</h2>
          <div className="space-y-2">
            {repairBreakdownData.map((segment) => (
              <div key={segment.type} className="flex items-center">
                <div className={`w-3 h-3 rounded-sm mr-2 ${segment.color}`}></div>
                <span className="text-sm text-slate-600 w-2/5 truncate" title={segment.type}>{segment.type}</span>
                <div className="w-2/5 bg-slate-200 rounded-full h-2.5 dark:bg-slate-700">
                  <div className={`${segment.color} h-2.5 rounded-full`} style={{ width: `${segment.percentage}%` }}></div>
                </div>
                <span className="text-sm text-slate-600 ml-2 w-1/5 text-right">{segment.percentage}% ({segment.count})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showQuickAppraisal && (
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <QuickAppraisalWidget
                onNavigateToFullBuyback={onNavigateToFullBuyback}
                buybackDeviceModels={buybackDeviceModels}
                buybackPricingFactors={buybackPricingFactors}
            />
          </div>
      )}

    </div>
  );
};
